export interface DiscreteTimeline<S> {
  history: S[]
  index: number
}

export interface AdvanceDiscreteTimelineOptions<S> {
  step: (state: S) => S
  isTerminal: (state: S) => boolean
}

function clampIndex(index: number, length: number): number {
  if (length <= 1) return 0
  if (index < 0) return 0
  if (index >= length) return length - 1
  return index
}

function assertHistory<S>(timeline: DiscreteTimeline<S>): asserts timeline is DiscreteTimeline<S> & { history: [S, ...S[]] } {
  if (timeline.history.length === 0) {
    throw new Error('DiscreteTimeline history must contain at least one state')
  }
}

export function createDiscreteTimeline<S>(initialState: S): DiscreteTimeline<S> {
  return { history: [initialState], index: 0 }
}

export function currentFromDiscreteTimeline<S>(timeline: DiscreteTimeline<S>): S {
  assertHistory(timeline)
  const safeIndex = clampIndex(timeline.index, timeline.history.length)
  return timeline.history[safeIndex]!
}

export function goToDiscreteStep<S>(
  timeline: DiscreteTimeline<S>,
  nextIndex: number,
): DiscreteTimeline<S> {
  assertHistory(timeline)
  if (nextIndex < 0 || nextIndex >= timeline.history.length) return timeline
  if (nextIndex === timeline.index) return timeline
  return { ...timeline, index: nextIndex }
}

export function resetDiscreteTimeline<S>(initialState: S): DiscreteTimeline<S> {
  return createDiscreteTimeline(initialState)
}

export function advanceDiscreteTimeline<S>(
  timeline: DiscreteTimeline<S>,
  opts: AdvanceDiscreteTimelineOptions<S>,
): DiscreteTimeline<S> {
  assertHistory(timeline)
  const safeIndex = clampIndex(timeline.index, timeline.history.length)
  const tailIndex = timeline.history.length - 1

  // If browsing history, move toward the tail without creating new states.
  if (safeIndex < tailIndex) {
    return { ...timeline, index: safeIndex + 1 }
  }

  const tail = timeline.history[tailIndex]!
  if (opts.isTerminal(tail)) {
    // Terminal tail stays stable under further step/tick calls.
    return safeIndex === timeline.index
      ? timeline
      : { ...timeline, index: safeIndex }
  }

  const next = opts.step(tail)
  const history = [...timeline.history, next]
  return { history, index: history.length - 1 }
}
